/**
 * Telegram Bot Setup Tool
 * A simpler alternative to WhatsApp that works immediately
 */

import React, { useState, useEffect } from 'react';
import { Check, X, AlertTriangle, Send, Copy, ExternalLink, RefreshCw, Bot, MessageSquare, Shield, Eye, EyeOff } from 'lucide-react';

interface SetupStep {
  id: string;
  title: string;
  description: string;
  command?: string;
  expectedOutput?: string;
  completed: boolean;
  status?: 'pending' | 'checking' | 'success' | 'error';
  errorMessage?: string;
}

interface ConfigStatus {
  telegramReady: boolean;
  botToken: boolean;
  webhookUrl: string | null;
  hasWebhookSecret: boolean;
  botInfo?: {
    id: number;
    username: string;
    first_name: string;
    can_join_groups: boolean;
    can_read_all_group_messages: boolean;
  };
}

export default function TelegramSetupTool() {
  const [currentSection, setCurrentSection] = useState(0);
  const [showToken, setShowToken] = useState(false);
  const [steps, setSteps] = useState<SetupStep[]>([
    // Section 1: Create Telegram Bot
    {
      id: 'create-bot',
      title: 'Open Telegram and find BotFather',
      description: 'Open Telegram app and search for @BotFather (official bot with blue checkmark)',
      completed: false
    },
    {
      id: 'start-botfather',
      title: 'Start conversation with BotFather',
      description: 'Send /start to BotFather',
      command: '/start',
      completed: false
    },
    {
      id: 'new-bot',
      title: 'Create new bot',
      description: 'Send /newbot command to create your bot',
      command: '/newbot',
      completed: false
    },
    {
      id: 'bot-name',
      title: 'Choose bot display name',
      description: 'Enter a friendly name for your bot (e.g., "My Business Bot")',
      completed: false
    },
    {
      id: 'bot-username',
      title: 'Choose bot username',
      description: 'Enter a unique username ending with "bot" (e.g., "mybusiness_bot")',
      completed: false
    },
    {
      id: 'save-token',
      title: 'Save bot token',
      description: 'Copy the token BotFather gives you (format: 123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11)',
      completed: false
    },
    
    // Section 2: Configure Cloudflare
    {
      id: 'add-token-secret',
      title: 'Add bot token to Cloudflare',
      description: 'Add your bot token as a secret in Cloudflare',
      command: 'npx wrangler secret put TELEGRAM_BOT_TOKEN',
      completed: false
    },
    {
      id: 'verify-token',
      title: 'Verify token is saved',
      description: 'Check that the token was saved successfully',
      command: 'npx wrangler secret list',
      expectedOutput: 'TELEGRAM_BOT_TOKEN',
      completed: false
    },
    
    // Section 3: Test Bot
    {
      id: 'check-config',
      title: 'Check configuration status',
      description: 'Verify Telegram is configured in the API',
      command: 'curl https://biig-api.b-9f2.workers.dev/api/telegram/test',
      completed: false
    },
    {
      id: 'get-bot-info',
      title: 'Get bot information',
      description: 'Verify bot token works by getting bot info',
      command: 'curl https://biig-api.b-9f2.workers.dev/api/telegram/info',
      completed: false
    },
    {
      id: 'start-chat',
      title: 'Start chat with your bot',
      description: 'Open Telegram, find your bot by username, and send /start',
      completed: false
    },
    {
      id: 'get-chat-id',
      title: 'Get your chat ID',
      description: 'Check Telegram getUpdates to find your chat ID',
      command: 'curl https://api.telegram.org/bot<YOUR_TOKEN>/getUpdates',
      completed: false
    }
  ]);

  const [botToken, setBotToken] = useState('');
  const [chatId, setChatId] = useState('');
  const [testMessage, setTestMessage] = useState('Hello from my Telegram bot! 🚀');
  const [sendStatus, setSendStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [sendResult, setSendResult] = useState<any>(null);
  const [configStatus, setConfigStatus] = useState<ConfigStatus | null>(null);
  const [isChecking, setIsChecking] = useState(false);

  const sections = [
    { title: 'Create Telegram Bot', steps: 6 },
    { title: 'Configure Cloudflare', steps: 2 },
    { title: 'Test Your Bot', steps: 4 }
  ];

  // Load saved state from database and localStorage
  useEffect(() => {
    // First try to load from database
    loadConfigFromDatabase();
    
    // Check configuration status on load
    checkConfigurationStatus();
  }, []);
  
  const loadConfigFromDatabase = async () => {
    try {
      const response = await fetch('https://biig-api.b-9f2.workers.dev/api/telegram/config');
      const data = await response.json();
      
      if (data.exists && data.config) {
        // Load from database
        const config = data.config;
        if (config.chatIds && config.chatIds.length > 0) {
          setChatId(config.chatIds[0]);
        }
        if (config.botTokenHint) {
          setBotToken(config.botTokenHint);
        }
        if (config.setupSteps) {
          const savedSteps = config.setupSteps;
          setSteps(prev => prev.map(step => ({
            ...step,
            completed: savedSteps[step.id]?.completed || false,
            status: savedSteps[step.id]?.status || 'pending'
          })));
        }
        
        // Also update config status with saved bot info
        if (config.botUsername || config.botName) {
          setConfigStatus(prev => prev ? {
            ...prev,
            botInfo: {
              ...prev.botInfo,
              username: config.botUsername || prev.botInfo?.username,
              first_name: config.botName || prev.botInfo?.first_name
            } as any
          } : prev);
        }
      } else {
        // Fall back to localStorage
        const savedState = localStorage.getItem('telegram-setup-state');
        if (savedState) {
          const state = JSON.parse(savedState);
          setSteps(state.steps || steps);
          setBotToken(state.botToken || '');
          setChatId(state.chatId || '');
          setCurrentSection(state.currentSection || 0);
        }
      }
    } catch (error) {
      console.error('Failed to load config from database:', error);
      // Fall back to localStorage
      const savedState = localStorage.getItem('telegram-setup-state');
      if (savedState) {
        const state = JSON.parse(savedState);
        setSteps(state.steps || steps);
        setBotToken(state.botToken || '');
        setChatId(state.chatId || '');
        setCurrentSection(state.currentSection || 0);
      }
    }
  };

  // Save state to localStorage and database
  useEffect(() => {
    // Save to localStorage
    localStorage.setItem('telegram-setup-state', JSON.stringify({
      steps,
      botToken,
      chatId,
      currentSection,
      timestamp: new Date().toISOString()
    }));
    
    // Save to database (debounced)
    const timeoutId = setTimeout(() => {
      saveConfigToDatabase();
    }, 1000);
    
    return () => clearTimeout(timeoutId);
  }, [steps, botToken, chatId, currentSection]);
  
  const saveConfigToDatabase = async () => {
    try {
      const setupSteps: any = {};
      steps.forEach(step => {
        setupSteps[step.id] = {
          completed: step.completed,
          status: step.status || 'pending'
        };
      });
      
      const config = {
        botUsername: configStatus?.botInfo?.username || null,
        botName: configStatus?.botInfo?.first_name || null,
        botTokenHint: botToken ? `${botToken.substring(0, 6)}...${botToken.slice(-4)}` : null,
        chatIds: chatId ? [chatId] : [],
        setupSteps
      };
      
      await fetch('https://biig-api.b-9f2.workers.dev/api/telegram/config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });
    } catch (error) {
      console.error('Failed to save config to database:', error);
    }
  };

  const checkConfigurationStatus = async () => {
    setIsChecking(true);
    try {
      // Check general config
      const response = await fetch('https://biig-api.b-9f2.workers.dev/api/telegram/test');
      const data = await response.json();
      
      // If configured, get bot info
      let botInfo = null;
      if (data.configured) {
        try {
          const infoResponse = await fetch('https://biig-api.b-9f2.workers.dev/api/telegram/info');
          const infoData = await infoResponse.json();
          if (infoData.ok && infoData.result) {
            botInfo = infoData.result;
          }
        } catch (e) {
          console.error('Failed to get bot info:', e);
        }
      }
      
      setConfigStatus({
        telegramReady: data.configured,
        botToken: data.configured,
        webhookUrl: data.webhookUrl,
        hasWebhookSecret: data.hasSecret,
        botInfo
      });
      
      // Auto-check completed items based on config
      if (data.configured) {
        setSteps(prev => prev.map(step => {
          if (step.id === 'add-token-secret' || step.id === 'verify-token' || step.id === 'check-config') {
            return { ...step, completed: true, status: 'success' };
          }
          if (step.id === 'save-token' && botToken) {
            return { ...step, completed: true, status: 'success' };
          }
          if (botInfo && (step.id === 'get-bot-info' || step.id === 'create-bot' || step.id === 'start-botfather' || step.id === 'new-bot' || step.id === 'bot-name' || step.id === 'bot-username')) {
            return { ...step, completed: true, status: 'success' };
          }
          return step;
        }));
      }
    } catch (error) {
      console.error('Failed to check configuration:', error);
    } finally {
      setIsChecking(false);
    }
  };

  const toggleStep = (stepId: string) => {
    setSteps(prev => prev.map(step => 
      step.id === stepId ? { ...step, completed: !step.completed } : step
    ));
  };

  const sendTestMessage = async () => {
    if (!chatId || !testMessage) {
      alert('Please enter both Chat ID and message');
      return;
    }

    setSendStatus('sending');
    setSendResult(null);

    try {
      const response = await fetch('https://biig-api.b-9f2.workers.dev/api/telegram/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: chatId,
          message: testMessage
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        setSendStatus('success');
        setSendResult(result);
        
        // Mark send test as complete
        setSteps(prev => prev.map(step => 
          step.id === 'start-chat' || step.id === 'get-chat-id' 
            ? { ...step, completed: true, status: 'success' } 
            : step
        ));
      } else {
        setSendStatus('error');
        setSendResult(result);
      }
    } catch (error) {
      setSendStatus('error');
      setSendResult({ error: error instanceof Error ? error.message : 'Failed to send' });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const resetSetup = async () => {
    if (confirm('This will reset all progress. Are you sure?')) {
      localStorage.removeItem('telegram-setup-state');
      setSteps(steps.map(s => ({ ...s, completed: false, status: 'pending' })));
      setBotToken('');
      setChatId('');
      setCurrentSection(0);
      setSendStatus('idle');
      setSendResult(null);
      setConfigStatus(null);
      
      // Clear database config
      try {
        await fetch('https://biig-api.b-9f2.workers.dev/api/telegram/config', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            botUsername: null,
            botName: null,
            botTokenHint: null,
            chatIds: [],
            setupSteps: {}
          })
        });
      } catch (error) {
        console.error('Failed to clear database config:', error);
      }
    }
  };

  const getSectionSteps = (sectionIndex: number) => {
    const startIndex = sections.slice(0, sectionIndex).reduce((acc, s) => acc + s.steps, 0);
    return steps.slice(startIndex, startIndex + sections[sectionIndex].steps);
  };

  const isSectionComplete = (sectionIndex: number) => {
    return getSectionSteps(sectionIndex).every(step => step.completed);
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Bot className="w-10 h-10" />
            <div>
              <h1 className="text-3xl font-bold">Telegram Bot Setup Tool</h1>
              <p className="text-blue-100 mt-1">Get messaging working in 5 minutes - no approval needed!</p>
            </div>
          </div>
          <button
            onClick={resetSetup}
            className="bg-white/20 hover:bg-white/30 transition-colors px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Reset
          </button>
        </div>
      </div>

      {/* Why Telegram? */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-blue-900 mb-2">Why Use Telegram Instead of WhatsApp?</h3>
        <div className="grid md:grid-cols-2 gap-3 text-sm text-blue-800">
          <div className="flex items-start gap-2">
            <Check className="w-4 h-4 text-green-600 mt-0.5" />
            <span>Instant setup - no business verification required</span>
          </div>
          <div className="flex items-start gap-2">
            <Check className="w-4 h-4 text-green-600 mt-0.5" />
            <span>Free forever - no monthly fees</span>
          </div>
          <div className="flex items-start gap-2">
            <Check className="w-4 h-4 text-green-600 mt-0.5" />
            <span>Works globally without restrictions</span>
          </div>
          <div className="flex items-start gap-2">
            <Check className="w-4 h-4 text-green-600 mt-0.5" />
            <span>Better file support (50MB limit)</span>
          </div>
        </div>
      </div>

      {/* Configuration Status */}
      {configStatus && (
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Configuration Status
            </h3>
            <button
              onClick={checkConfigurationStatus}
              disabled={isChecking}
              className="text-blue-600 hover:text-blue-700 flex items-center gap-2 text-sm"
            >
              <RefreshCw className={`w-4 h-4 ${isChecking ? 'animate-spin' : ''}`} />
              {isChecking ? 'Checking...' : 'Refresh'}
            </button>
          </div>
          
          <div className="grid md:grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              {configStatus.telegramReady ? (
                <Check className="w-5 h-5 text-green-600" />
              ) : (
                <X className="w-5 h-5 text-red-600" />
              )}
              <span className="text-sm">Bot Token: {configStatus.botToken ? 'Configured' : 'Not Set'}</span>
            </div>
            
            {configStatus.botInfo && (
              <>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <span className="text-sm">Bot Username: @{configStatus.botInfo.username}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <span className="text-sm">Bot Name: {configStatus.botInfo.first_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <span className="text-sm">Bot ID: {configStatus.botInfo.id}</span>
                </div>
              </>
            )}
            
            {configStatus.webhookUrl && (
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-600" />
                <span className="text-sm">Webhook: {configStatus.webhookUrl}</span>
              </div>
            )}
          </div>
          
          {configStatus.telegramReady && configStatus.botInfo && (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded">
              <p className="text-sm text-green-800">
                ✅ Your bot <strong>@{configStatus.botInfo.username}</strong> is ready! 
                Open Telegram and start a chat with your bot to get your Chat ID.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Progress Sections */}
      <div className="space-y-4 mb-8">
        {sections.map((section, sectionIndex) => {
          const sectionSteps = getSectionSteps(sectionIndex);
          const isComplete = isSectionComplete(sectionIndex);
          const isActive = currentSection === sectionIndex;
          
          return (
            <div 
              key={sectionIndex}
              className={`border rounded-lg overflow-hidden transition-all ${
                isActive ? 'border-blue-400 shadow-lg' : 'border-gray-200'
              }`}
            >
              <button
                onClick={() => setCurrentSection(sectionIndex)}
                className={`w-full px-6 py-4 flex items-center justify-between transition-colors ${
                  isActive ? 'bg-blue-50' : 'bg-white hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${
                    isComplete ? 'bg-green-600 text-white' : 
                    isActive ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
                  }`}>
                    {isComplete ? <Check className="w-5 h-5" /> : sectionIndex + 1}
                  </div>
                  <span className="font-semibold text-left">{section.title}</span>
                </div>
                <span className="text-sm text-gray-500">
                  {sectionSteps.filter(s => s.completed).length} / {section.steps} steps
                </span>
              </button>
              
              {isActive && (
                <div className="border-t border-gray-200 bg-white p-6">
                  <div className="space-y-3">
                    {sectionSteps.map((step) => (
                      <div 
                        key={step.id}
                        className={`p-4 rounded-lg border transition-all ${
                          step.completed 
                            ? 'bg-green-50 border-green-200' 
                            : 'bg-white border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => toggleStep(step.id)}
                                className={`w-6 h-6 rounded flex items-center justify-center transition-colors ${
                                  step.completed 
                                    ? 'bg-green-600 text-white' 
                                    : 'border-2 border-gray-300 hover:border-gray-400'
                                }`}
                              >
                                {step.completed && <Check className="w-4 h-4" />}
                              </button>
                              <h4 className="font-medium">{step.title}</h4>
                            </div>
                            <p className="text-gray-600 text-sm mt-2 ml-9">{step.description}</p>
                            
                            {step.command && (
                              <div className="mt-3 ml-9">
                                <div className="bg-gray-900 text-gray-100 p-3 rounded font-mono text-sm flex items-center justify-between">
                                  <span>{step.command}</span>
                                  <button
                                    onClick={() => step.command && copyToClipboard(step.command)}
                                    className="text-gray-400 hover:text-white transition-colors"
                                  >
                                    <Copy className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            )}
                            
                            {step.expectedOutput && (
                              <div className="mt-2 ml-9">
                                <p className="text-xs text-gray-500 mb-1">Expected output:</p>
                                <div className="bg-gray-100 p-2 rounded text-sm font-mono">
                                  {step.expectedOutput}
                                </div>
                              </div>
                            )}
                            
                            {/* Special input for bot token */}
                            {step.id === 'save-token' && (
                              <div className="mt-3 ml-9">
                                <div className="relative">
                                  <input
                                    type={showToken ? "text" : "password"}
                                    placeholder="Paste your bot token here"
                                    value={botToken}
                                    onChange={(e) => setBotToken(e.target.value)}
                                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-400"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setShowToken(!showToken)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                                  >
                                    {showToken ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                  </button>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                  Format: 123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11
                                </p>
                              </div>
                            )}
                            
                            {/* Special input for chat ID */}
                            {step.id === 'get-chat-id' && (
                              <div className="mt-3 ml-9">
                                <input
                                  type="text"
                                  placeholder="Enter your Chat ID (e.g., 123456789)"
                                  value={chatId}
                                  onChange={(e) => setChatId(e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-400"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                  To get your Chat ID: Send a message to your bot, then check getUpdates API or use @userinfobot
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Test Message Sender */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <MessageSquare className="w-5 h-5" />
          Send Test Message
        </h3>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Chat ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={chatId}
              onChange={(e) => setChatId(e.target.value)}
              placeholder="e.g., 123456789"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-400"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Test Message
            </label>
            <input
              type="text"
              value={testMessage}
              onChange={(e) => setTestMessage(e.target.value)}
              placeholder="Enter your test message"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-400"
            />
          </div>
        </div>
        
        <button
          onClick={sendTestMessage}
          disabled={sendStatus === 'sending' || !chatId}
          className={`mt-4 px-6 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
            sendStatus === 'sending' 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
        >
          <Send className="w-4 h-4" />
          {sendStatus === 'sending' ? 'Sending...' : 'Send Test Message'}
        </button>
        
        {sendResult && (
          <div className={`mt-4 p-4 rounded-lg ${
            sendStatus === 'success' 
              ? 'bg-green-50 border border-green-200' 
              : 'bg-red-50 border border-red-200'
          }`}>
            {sendStatus === 'success' ? (
              <div>
                <p className="text-green-800 font-medium flex items-center gap-2">
                  <Check className="w-5 h-5" />
                  Message sent successfully!
                </p>
                {sendResult.messageId && (
                  <p className="text-sm text-green-700 mt-1">
                    Message ID: {sendResult.messageId}
                  </p>
                )}
              </div>
            ) : (
              <div>
                <p className="text-red-800 font-medium flex items-center gap-2">
                  <X className="w-5 h-5" />
                  Failed to send message
                </p>
                <p className="text-sm text-red-700 mt-1">
                  {sendResult.error || 'Unknown error occurred'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Quick Reference */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">Quick Reference</h3>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Useful Commands</h4>
            <div className="space-y-2 text-sm">
              <div className="font-mono bg-white p-2 rounded border border-gray-200">
                npx wrangler secret put TELEGRAM_BOT_TOKEN
              </div>
              <div className="font-mono bg-white p-2 rounded border border-gray-200">
                npx wrangler secret list
              </div>
              <div className="font-mono bg-white p-2 rounded border border-gray-200">
                npx wrangler deploy
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="font-medium text-gray-900 mb-2">API Endpoints</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="font-mono">POST /api/telegram/send</span>
                <span className="text-gray-500">Send message</span>
              </div>
              <div className="flex justify-between">
                <span className="font-mono">GET /api/telegram/info</span>
                <span className="text-gray-500">Get bot info</span>
              </div>
              <div className="flex justify-between">
                <span className="font-mono">POST /api/telegram/webhook</span>
                <span className="text-gray-500">Receive updates</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h4 className="font-medium text-yellow-900 mb-2 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Important Notes
          </h4>
          <ul className="text-sm text-yellow-800 space-y-1">
            <li>• Users must start a conversation with your bot before you can message them</li>
            <li>• Keep your bot token secret - never commit it to git</li>
            <li>• Chat IDs are unique per user and bot combination</li>
            <li>• Telegram messages support markdown formatting</li>
          </ul>
        </div>
        
        <div className="mt-6 flex gap-4">
          <a
            href="https://core.telegram.org/bots/api"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-700 flex items-center gap-2 text-sm"
          >
            <ExternalLink className="w-4 h-4" />
            Telegram Bot API Documentation
          </a>
          <a
            href="https://t.me/BotFather"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-700 flex items-center gap-2 text-sm"
          >
            <ExternalLink className="w-4 h-4" />
            Open BotFather
          </a>
        </div>
      </div>
    </div>
  );
}